/**
 * Router Agent
 * 
 * Presents the main menu of 4 options and detects user intent.
 * Routes to the appropriate specialized agent based on selection.
 * 
 * Options:
 * 1. FAQ/Questions about SAAED services
 * 2. Accident Report
 * 3. Report Renewal
 * 4. Lost Report Replacement
 * 
 * NOTE: System instructions and tools are defined in backend (backend/config/agents.py)
 * This file only contains tool handlers, guardrails, and metadata.
 */

import { AgentNode, AgentContext, ToolCallResult, SupportedLanguage, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

/**
 * Map option to target agent
 */
const optionToAgent: Record<string, string> = {
  'option_1': 'faq_agent',
  'option_2': 'accident_start_agent',
  'option_3': 'renewal_input_agent',
  'option_4': 'lost_report_upload_extract_agent',
};

/**
 * Options that are not yet implemented (coming soon)
 */
const comingSoonOptions = new Set<string>();

/**
 * Coming soon messages in different languages
 */
const comingSoonMessages: Record<SupportedLanguage, string> = {
  ar: 'عذراً، هذه الخدمة قيد التطوير وستكون متاحة قريباً. الرجاء اختيار خدمة أخرى.',
  en: 'Sorry, this service is under development and will be available soon. Please choose another option.',
  ur: 'معذرت، یہ سروس ترقی میں ہے اور جلد دستیاب ہوگی۔ براہ کرم دوسرا آپشن منتخب کریں۔',
};

/**
 * Create the Router Agent node
 * System instructions and tools are provided by backend via ephemeral token
 */
export function createRouterAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'router_agent',
    name: 'Router Agent',
    description: 'Presents main menu and routes to specialized agents',
    
    // Initial prompt to trigger the AI to present the menu
    // Backend provides the actual system instruction via ephemeral token
    initialPrompt: 'Present the 4 main menu options to the user in their preferred language. Welcome them and explain each option clearly.',
    
    // Must have language detected
    entryGuardrails: requireLanguage(),
    
    // Exit to various agents based on option
    exitGuardrails: {
      transitions: new Map([
        ['option_1', {
          target: { agentId: 'faq_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['option_2', {
          target: { agentId: 'accident_start_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['option_3', {
          target: { agentId: 'renewal_input_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['option_4', {
          target: { agentId: 'lost_report_upload_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['redirect_faq', {
          target: { agentId: 'faq_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'greeting_agent' }, // Fallback to start
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      // Redirect to FAQ agent with the question
      if (toolName === 'redirect_to_faq') {
        const question = args.question || '';
        
        console.log('[RouterAgent] Redirecting to FAQ agent with question:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Redirecting to FAQ agent to answer the question.',
          },
          triggersTransition: true,
          transitionTo: 'faq_agent',
          transitionData: {
            pendingQuestion: question, // Pass the question to FAQ agent
          },
        };
      }
      
      if (toolName === 'detect_user_intent') {
        const selectedOption = args.selected_option as string;
        const confidence = args.confidence || 'high';
        const targetAgent = optionToAgent[selectedOption];
        const language = context.transitionData.language || 'en';
        
        console.log('[RouterAgent] Intent detected:', selectedOption, 'confidence:', confidence);
        
        if (!targetAgent) {
          return {
            toolName,
            args,
            result: { success: false, error: 'Invalid option' },
          };
        }
        
        // Handle coming soon options (2 and 4)
        if (comingSoonOptions.has(selectedOption)) {
          console.log('[RouterAgent] Coming soon option selected:', selectedOption);
          const message = comingSoonMessages[language as SupportedLanguage] || comingSoonMessages.en;
          
          return {
            toolName,
            args,
            result: { 
              success: true, 
              coming_soon: true,
              message: message,
              speak_message: message, // Tell the AI to speak this message
            },
            // Don't trigger transition - stay in router and let AI speak the message
            triggersTransition: false,
          };
        }
        
        // Normal transition for implemented options
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: `Routing to ${selectedOption}`,
            targetAgent,
          },
          triggersTransition: true,
          transitionTo: targetAgent,
          transitionData: {
            customData: { selectedOption },
          },
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[RouterAgent] Activated - language:', context.transitionData.language);
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[RouterAgent] Deactivated - selected:', context.transitionData.customData?.selectedOption);
    },
  };
}

export default createRouterAgent;

